import { Fragment, useRef, useState } from 'react'
import { Loader2, Stethoscope, AlertTriangle } from 'lucide-react'
import { postJson } from '../lib/api.js'
import ConfirmDialog from './ConfirmDialog.jsx'

/**
 * "สร้างการรักษา" — turn an appointment into a treatment (+ its chart).
 *
 * Shared by the ประวัติ list on the patient page and the appointment edit modal
 * (which the clinic-wide /appointments page opens too), so both entry points
 * ask the same question and post the same body.
 *
 * Two-stage confirm by design:
 *   1. normal      → "สร้างการรักษาจากนัดหมายนี้?" (one appointment → one treatment)
 *   2. duplicate   → the appointment already has a live treatment. That is a
 *      real case (a visit split across two payment methods needs two
 *      treatments), so it is a second, louder confirm rather than a refusal.
 *
 * The server decides, not this component: a create with no `bypass` comes back
 * `{created:false, existing_count}` when a treatment already exists, which
 * flips us to stage 2. So two people clicking at once still can't silently
 * mint a duplicate — the loser gets asked.
 */
// api.js throws `HTTP <status>: <body>`. Xano error bodies are
// {code, message, payload} and this proxy's are {error, detail} — dig the human
// sentence back out so a failed create doesn't show staff a raw JSON blob.
function humanError(e) {
  const raw = String(e?.message || '')
  if (e?.status === 403) return 'บัญชีนี้ไม่มีสิทธิ์สร้างการรักษา'
  const m = raw.match(/\{[\s\S]*\}\s*$/)
  if (m) {
    try {
      const j = JSON.parse(m[0])
      const msg = j.message || j.detail || j.error
      if (msg) return String(msg)
    } catch { /* not JSON — fall through to the raw text */ }
  }
  return raw || 'สร้างการรักษาไม่สำเร็จ'
}

// วันที่ / รายการ / หมอ / สาขา as a label column + a value column. Labelled beats
// concatenated: staff are checking they are about to bill the right visit.
function Facts({ fields }) {
  if (!fields.length) return null
  return (
    <span className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
      {fields.map((f) => (
        <Fragment key={f.label}>
          <span className="text-slate-400">{f.label}</span>
          <span className="text-slate-700">{f.value}</span>
        </Fragment>
      ))}
    </span>
  )
}

function Warnings({ warnings }) {
  if (!warnings.length) return null
  return (
    <span className="mt-2 block space-y-1">
      {warnings.map((w) => (
        <span key={w} className="flex items-start gap-1.5 text-amber-700">
          <AlertTriangle size={13} className="flex-none mt-0.5" />{w}
        </span>
      ))}
    </span>
  )
}

export default function CreateTreatmentButton({
  appointmentId,
  fields = [],           // [{label, value}] — วันที่ / รายการ / หมอ / สาขา
  warnings = [],         // plain sentences: not-today, cancelled, no-show
  existingCount = 0,     // known-existing live treatments for this appointment
  onCreated,             // (res) => void — res = { treatment_id, chart_id, patient_id, … }
  variant = 'row',       // 'row' (compact, card) | 'icon' (square, table) | 'modal' (footer button)
  disabled = false,
}) {
  const [stage, setStage] = useState(null) // null | 'confirm' | 'duplicate'
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [dupCount, setDupCount] = useState(existingCount)
  const inflight = useRef(false) // survives a double-click within one tick

  function open(e) {
    e?.stopPropagation?.()
    setErr(null)
    setDupCount(existingCount)
    setStage(existingCount > 0 ? 'duplicate' : 'confirm')
  }

  async function run(bypass) {
    if (inflight.current) return
    inflight.current = true
    setBusy(true); setErr(null)
    try {
      const res = await postJson('/api/treatment-from-appointment', {
        appointment_id: Number(appointmentId),
        ...(bypass ? { bypass: true } : {}),
      })
      if (res?.created) {
        setStage(null)
        onCreated?.(res)
        return
      }
      // Not created = something already exists. Ask again, louder.
      setDupCount(Number(res?.existing_count) || 1)
      setStage('duplicate')
    } catch (e) {
      setErr(humanError(e))
    } finally {
      inflight.current = false
      setBusy(false)
    }
  }

  const summary = (
    <span className="block">
      <Facts fields={fields} />
      <Warnings warnings={warnings} />
      <span className="block mt-3 text-slate-500">
        จะสร้างการรักษา 1 รายการ พร้อม chart ของเคสนี้
      </span>
      {err && (
        <span className="mt-2 flex items-start gap-1.5 text-rose-600">
          <AlertTriangle size={13} className="flex-none mt-0.5" />{err}
        </span>
      )}
    </span>
  )

  const dupSummary = (
    <span className="block">
      <span className="block text-slate-700">
        นัดหมายนี้มีการรักษาแล้ว {dupCount} รายการ
      </span>
      <Facts fields={fields} />
      <span className="block mt-3 text-slate-500">
        ถ้าเข้ามาดูเฉยๆ ให้กดยกเลิก
      </span>
      <span className="block mt-1 text-slate-500">
        สร้างเพิ่มเมื่อต้องแยกรายการจริงๆ เช่น คนไข้ชำระเงินหลายวิธีในครั้งเดียว
      </span>
      {err && (
        <span className="mt-2 flex items-start gap-1.5 text-rose-600">
          <AlertTriangle size={13} className="flex-none mt-0.5" />{err}
        </span>
      )}
    </span>
  )

  // Colour IS the state here: brand-blue = this appointment has no treatment yet
  // (the normal action), muted slate = it already has one, so the button should
  // RECEDE rather than shout — the green chart icon beside it is what says
  // "already done", and the confirm dialog carries the warning. Amber was tried
  // first and read as an alarm on every historical row.
  const has = existingCount > 0
  const tone = has
    // Ghost, not a second solid button: no visible ring and no fill, so the blue
    // one is unmistakably the louder of the two. The affordance comes back on hover.
    ? 'text-slate-400 ring-transparent bg-transparent hover:bg-slate-100 hover:text-slate-600'
    : 'text-brand-700 ring-brand-200 bg-brand-50/60 hover:bg-brand-100'
  // whitespace-nowrap: the label wrapped to two lines in the narrow การรักษา
  // column, which made every row a different height. In the table it is now the
  // 'icon' variant instead — a square that matches the chart icons beside it and
  // hands the width back to รายละเอียด; the tooltip carries the label.
  const isIcon = variant === 'icon'
  const btnCls = variant === 'modal'
    ? `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[13.5px] font-medium ring-1 whitespace-nowrap disabled:opacity-50 ${tone}`
    : isIcon
      ? `inline-flex items-center justify-center w-7 h-7 rounded-lg ring-1 transition-colors disabled:opacity-50 ${tone}`
      : `inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[12.5px] font-medium ring-1 whitespace-nowrap disabled:opacity-50 ${tone}`

  return (
    <>
      <button
        type="button"
        onClick={open}
        disabled={disabled || busy}
        className={btnCls}
        aria-label="สร้างการรักษา"
        title={has ? 'นัดหมายนี้สร้างการรักษาไปแล้ว — กดเพื่อดูรายละเอียด/สร้างเพิ่ม' : 'สร้างการรักษา + chart จากนัดหมายนี้'}
      >
        {busy ? <Loader2 size={isIcon ? 14 : 13} className="animate-spin" /> : <Stethoscope size={isIcon ? 14 : 13} />}
        {!isIcon && 'สร้างการรักษา'}
      </button>

      {stage === 'confirm' && (
        <ConfirmDialog
          title="สร้างการรักษาจากนัดหมายนี้?"
          message={summary}
          confirmLabel={busy ? 'กำลังสร้าง…' : 'สร้างการรักษา'}
          onConfirm={() => run(false)}
          onCancel={() => { if (!busy) { setStage(null); setErr(null) } }}
        />
      )}

      {stage === 'duplicate' && (
        <ConfirmDialog
          tone="danger"
          title="นัดหมายนี้สร้างการรักษาไปแล้ว"
          message={dupSummary}
          confirmLabel={busy ? 'กำลังสร้าง…' : 'ยืนยันสร้างเพิ่ม'}
          onConfirm={() => run(true)}
          onCancel={() => { if (!busy) { setStage(null); setErr(null) } }}
        />
      )}
    </>
  )
}
